import { useState, useEffect, useCallback, useRef } from 'react';
import { apiClient } from '../../contexts/AuthContext';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Camera, FileText, Image, CheckCircle, Share2 } from 'lucide-react';
import { toast } from 'sonner';
import { Spinner } from './ChallengeShared';
import { ShareButtonRow, shareFile } from './ShareButtons';

export function InscriptionTab() {
  const [participants, setParticipants] = useState([]);
  const [selectedNom, setSelectedNom] = useState('');
  const [selectedPid, setSelectedPid] = useState('');
  const [form, setForm] = useState({ poids_j0: '', mg_j0: '', mm_j0: '', objectif: 'gras' });
  const [preview, setPreview] = useState(null);
  const [saving, setSaving] = useState(false);
  const [engagements, setEngagements] = useState([false, false, false, false, false, false, false]);
  const [photo, setPhoto] = useState(null);
  const [photoPreview, setPhotoPreview] = useState(null);
  const fileInputRef = useRef(null);
  const videoRef = useRef(null);
  const [showCamera, setShowCamera] = useState(false);

  useEffect(() => {
    apiClient.get('/challenge/participants').then(r => setParticipants(r.data)).catch(() => {});
  }, []);

  const ENGAGEMENTS_TEXT = [
    "Je m'engage a effectuer les 4 versements de 5 000 XPF aux dates indiquees",
    "Je m'engage a peser aux 6 dates officielles, a jeun, meme balance, meme heure",
    "Je m'engage a publier dans le groupe Facebook prive (repas/sport/pesees)",
    "J'accepte que les contenus soient utilises dans la communication La Meute",
    "J'ai lu et compris les regles du challenge (3 prix, garde-fous, anti-cumul)",
    "Je certifie que les mesures J0 sont exactes (conditions officielles)",
    "Je m'engage a participer a la photo de groupe officielle (J0 et/ou S12)"
  ];
  const allChecked = engagements.every(Boolean);

  const toggleEngagement = (i) => {
    const copy = [...engagements];
    copy[i] = !copy[i];
    setEngagements(copy);
  };

  const computePreview = useCallback(async () => {
    const p = parseFloat(form.poids_j0), mm = parseFloat(form.mm_j0);
    if (!p || !mm || p <= 0 || mm <= 0) { setPreview(null); return; }
    let pct = 4;
    try {
      const { data } = await apiClient.get(`/challenge/preview?poids=${p}&mg=${parseFloat(form.mg_j0)||0}&mm=${mm}&objectif=${form.objectif}&pct=${pct}`);
      setPreview(data);
    } catch { setPreview(null); }
  }, [form]);

  useEffect(() => { const t = setTimeout(computePreview, 500); return () => clearTimeout(t); }, [computePreview]);

  const handleSelectParticipant = (nom) => {
    setSelectedNom(nom);
    const p = participants.find(x => x.nom === nom);
    if (p) setSelectedPid(p.id);
  };

  const handleFilePhoto = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (ev) => { setPhoto(ev.target.result); setPhotoPreview(ev.target.result); };
    reader.readAsDataURL(file);
  };

  const startCamera = async () => {
    setShowCamera(true);
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ video: { facingMode: 'user' }, audio: false });
      if (videoRef.current) videoRef.current.srcObject = stream;
    } catch { toast.error('Camera non disponible'); setShowCamera(false); }
  };

  const capturePhoto = () => {
    if (!videoRef.current) return;
    const canvas = document.createElement('canvas');
    canvas.width = videoRef.current.videoWidth;
    canvas.height = videoRef.current.videoHeight;
    canvas.getContext('2d').drawImage(videoRef.current, 0, 0);
    const dataUrl = canvas.toDataURL('image/jpeg', 0.8);
    setPhoto(dataUrl);
    setPhotoPreview(dataUrl);
    const stream = videoRef.current.srcObject;
    if (stream) stream.getTracks().forEach(t => t.stop());
    setShowCamera(false);
  };

  const handleInscription = async () => {
    if (!selectedNom || !form.poids_j0 || !form.mg_j0 || !form.mm_j0) { toast.error('Selectionne un participant et remplis les mesures J0'); return; }
    if (!allChecked) { toast.error('Tous les engagements doivent etre coches'); return; }
    setSaving(true);
    try {
      const { data } = await apiClient.post('/challenge/inscription', {
        nom: selectedNom,
        poids_j0: parseFloat(form.poids_j0), mg_j0: parseFloat(form.mg_j0), mm_j0: parseFloat(form.mm_j0),
        objectif: form.objectif
      });
      const pid = data.participant_id;
      if (photo && pid) {
        try { await apiClient.post(`/challenge/participants/${pid}/photo`, { photo }); } catch {}
      }
      toast.success(`${selectedNom} inscrit au challenge !`);
      setSelectedPid(pid);
    } catch (e) { toast.error(e.response?.data?.detail || 'Erreur'); }
    finally { setSaving(false); }
  };

  const downloadPDF = async () => {
    const pid = selectedPid || participants.find(x => x.nom === selectedNom)?.id;
    if (!pid) { toast.error('Inscris d\'abord le participant'); return; }
    try {
      const { data } = await apiClient.get(`/challenge/inscription-pdf/${pid}`, { responseType: 'blob' });
      const blob = new Blob([data], { type: 'application/pdf' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `attestation_${selectedNom}.pdf`;
      a.click();
      toast.success('Attestation telechargee !');
    } catch { toast.error('Erreur generation PDF'); }
  };

  const sharePDF = async () => {
    const pid = selectedPid || participants.find(x => x.nom === selectedNom)?.id;
    if (!pid) { toast.error('Inscris d\'abord le participant'); return; }
    try {
      const { data } = await apiClient.get(`/challenge/inscription-pdf/${pid}`, { responseType: 'blob' });
      const blob = new Blob([data], { type: 'application/pdf' });
      const shared = await shareFile({ blob, fileName: `attestation_${selectedNom}.pdf`, title: `Attestation Challenge - ${selectedNom}`, text: `Acte d'engagement Challenge Transformation La Meute - ${selectedNom}` });
      if (!shared) {
        downloadPDF();
        toast.info('Partage natif non disponible — fichier telecharge');
      }
    } catch { toast.error('Erreur partage PDF'); }
  };

  return (
    <div className="p-4 pb-24 space-y-4" data-testid="inscription-tab">
      <div className="bg-[#1B1B1B] border border-[#2A2A2E] rounded-lg p-5">
        <h3 className="text-lg font-bold text-white mb-4" style={{ fontFamily: "'Bebas Neue', sans-serif" }}>Inscrire un participant</h3>
        <div className="space-y-3">
          <select value={selectedNom} onChange={e => handleSelectParticipant(e.target.value)}
            className="w-full bg-[#0F0F0F] border border-[#2A2A2E] text-white rounded-md px-3 py-2 text-sm" data-testid="insc-select-participant">
            <option value="">Choisir un participant...</option>
            {participants.filter(p => !p.inscription_validee).map(p => <option key={p.id} value={p.nom}>{p.nom}</option>)}
          </select>

          <div>
            <Label className="text-[#AAAAAA] text-xs mb-2 block">Photo du participant</Label>
            <div className="flex gap-2">
              {photoPreview ? (
                <div className="relative w-20 h-20 rounded-lg overflow-hidden border border-[#2A2A2E]">
                  <img src={photoPreview} alt="Photo" className="w-full h-full object-cover" />
                  <button onClick={() => { setPhoto(null); setPhotoPreview(null); }} className="absolute top-0.5 right-0.5 w-5 h-5 bg-red-500 rounded-full flex items-center justify-center text-white text-xs">x</button>
                </div>
              ) : (
                <>
                  <button onClick={startCamera} className="flex-1 flex items-center justify-center gap-2 py-3 bg-[#0F0F0F] border border-[#2A2A2E] rounded-lg text-sm text-[#AAAAAA] hover:border-[#FF6B35]/40" data-testid="btn-camera">
                    <Camera className="w-4 h-4" /> Prendre photo
                  </button>
                  <button onClick={() => fileInputRef.current?.click()} className="flex-1 flex items-center justify-center gap-2 py-3 bg-[#0F0F0F] border border-[#2A2A2E] rounded-lg text-sm text-[#AAAAAA] hover:border-[#FF6B35]/40">
                    <Image className="w-4 h-4" /> Importer
                  </button>
                  <input ref={fileInputRef} type="file" accept="image/*" className="hidden" onChange={handleFilePhoto} />
                </>
              )}
            </div>
            {showCamera && (
              <div className="mt-2 relative rounded-lg overflow-hidden">
                <video ref={videoRef} autoPlay playsInline className="w-full rounded-lg" />
                <button onClick={capturePhoto} className="absolute bottom-2 left-1/2 -translate-x-1/2 px-6 py-2 bg-[#FF6B35] text-white rounded-full text-sm font-bold">Capturer</button>
              </div>
            )}
          </div>

          <div className="grid grid-cols-3 gap-2">
            <div><Label className="text-[#AAAAAA] text-xs">Poids (kg)</Label><Input type="number" step="0.1" value={form.poids_j0} onChange={e => setForm({...form, poids_j0: e.target.value})} className="bg-[#0F0F0F] border-[#2A2A2E] text-white font-mono" data-testid="insc-poids" /></div>
            <div><Label className="text-[#AAAAAA] text-xs">Gras (%)</Label><Input type="number" step="0.1" value={form.mg_j0} onChange={e => setForm({...form, mg_j0: e.target.value})} className="bg-[#0F0F0F] border-[#2A2A2E] text-white font-mono" /></div>
            <div><Label className="text-[#AAAAAA] text-xs">Muscle (kg)</Label><Input type="number" step="0.1" value={form.mm_j0} onChange={e => setForm({...form, mm_j0: e.target.value})} className="bg-[#0F0F0F] border-[#2A2A2E] text-white font-mono" /></div>
          </div>

          <div><Label className="text-[#AAAAAA] text-xs">Objectif</Label>
            <select value={form.objectif} onChange={e => setForm({...form, objectif: e.target.value})} className="w-full bg-[#0F0F0F] border border-[#2A2A2E] text-white rounded-md px-3 py-2 text-sm">
              <option value="gras">Perte de gras</option>
              <option value="muscle">Prise de muscle</option>
            </select>
            <p className="text-[10px] text-zinc-500 mt-1.5">Regle unique pour tous : poids −4% OU gras −4% OU muscle +4% (au prorata des paliers S4/S8/S12)</p>
          </div>

          {preview && !preview.error && (
            <div className="bg-[#FF6B35]/10 border border-[#FF6B35]/30 rounded-md p-3" data-testid="inscription-preview">
              <p className="text-sm text-[#FF6B35] font-semibold mb-2">{form.objectif === 'gras' ? preview.resume_gras : preview.resume_muscle}</p>
              <div className="grid grid-cols-4 gap-1 text-center">
                {preview.paliers?.map((pal, i) => (
                  <div key={i} className="bg-[#0F0F0F] rounded-md p-1.5"><p className="text-[10px] text-[#AAAAAA]">{pal.label}</p><p className="font-mono text-xs text-white">{pal.poids_cible}kg</p></div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>

      <div className="bg-[#1B1B1B] border border-[#2A2A2E] rounded-lg p-5" data-testid="engagements-section">
        <h3 className="text-lg font-bold text-white mb-3" style={{ fontFamily: "'Bebas Neue', sans-serif" }}>Engagements obligatoires</h3>
        <div className="space-y-2">
          {ENGAGEMENTS_TEXT.map((text, i) => (
            <button key={i} onClick={() => toggleEngagement(i)}
              className={`w-full flex items-start gap-3 p-3 rounded-md text-left transition-colors ${engagements[i] ? 'bg-[#2e7d32]/15 border border-[#2e7d32]/30' : 'bg-[#0F0F0F] border border-[#2A2A2E]'}`}
              data-testid={`engagement-${i}`}>
              <div className={`w-5 h-5 rounded-sm flex-shrink-0 flex items-center justify-center mt-0.5 ${engagements[i] ? 'bg-[#2e7d32] text-white' : 'border-2 border-zinc-500'}`}>
                {engagements[i] && <CheckCircle className="w-3.5 h-3.5" />}
              </div>
              <span className={`text-xs ${engagements[i] ? 'text-white' : 'text-[#AAAAAA]'}`}>{text}</span>
            </button>
          ))}
        </div>
        <p className={`text-xs mt-3 text-center font-semibold ${allChecked ? 'text-[#2e7d32]' : 'text-zinc-500'}`}>
          {allChecked ? 'Tous les engagements sont acceptes' : `${engagements.filter(Boolean).length}/7 engagements coches`}
        </p>
      </div>

      <div className="space-y-2">
        <Button onClick={handleInscription} disabled={saving || !allChecked} className="w-full bg-gradient-meute text-white hover:opacity-90 disabled:opacity-40" data-testid="btn-inscription">
          {saving ? 'Enregistrement...' : 'Valider l\'inscription'}
        </Button>
        {selectedPid && (
          <div className="bg-[#1B1B1B] border border-[#2A2A2E] rounded-lg p-4">
            <p className="text-xs text-zinc-400 font-semibold mb-3 flex items-center gap-2">
              <Share2 className="w-3.5 h-3.5 text-[#FF6B35]" /> Partager l'attestation PDF
            </p>
            <ShareButtonRow
              onDownload={downloadPDF}
              shareText={`Acte d'engagement Challenge Transformation La Meute Polynesie - ${selectedNom}. Telecharge ton attestation sur l'app La Meute !`}
              onShareNative={sharePDF}
            />
          </div>
        )}
      </div>
    </div>
  );
}
